/* ===================== RESCHEDULE LIVE CLASS MODAL =====================
   Popup so instructors can move a scheduled class to a new date/time —
   PUT /live-classes/:id with just start_time/end_time (every field on the
   update route is optional server-side; the same start-now-or-later /
   end-after-start rules as create apply to whatever is sent). Only offered
   for scheduled (not on-air / not completed) sessions — the buttons that
   open it are gated with isScheduledRow.

   Same form idiom as CreateLiveClassModal: react-hook-form (@astro/forms),
   the dashboard's Field/input skin, Laravel 422 field errors mapped inline
   via setError. Success invalidates ['dashboard','live-classes'] (Academy
   stats + lists) and ['dashboard','course-detail'] (embedded course rows),
   then closes. */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@astro/api'
import { Controller, useForm } from '@astro/forms'
import { DateTimeField } from '@astro/date-field'

const inputCls =
  'w-full bg-stone-50 border rounded-xl px-4 py-3 text-sm text-astroDark focus:outline-none focus:border-goldAccent/50 focus:bg-white transition-colors'

function Field({ label, error, className = '', children }) {
  return (
    <div className={className}>
      <label className="block text-[9px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">{label}</label>
      {children}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  )
}

/** "YYYY-MM-DDTHH:mm" (datetime-local) → "YYYY-MM-DD HH:mm:00" (API). */
const toApiDateTime = (v) => (v ? `${String(v).replace('T', ' ')}:00` : '')

/** The current minute in datetime-local form — for min= and the
 * not-in-the-past rule. Same-format strings compare lexicographically. */
function nowLocal() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** `c` is the normalised class row (useLiveClasses) being rescheduled —
 * startLocal/endLocal prefill the pickers with the current schedule. */
export default function RescheduleLiveClassModal({ c, onClose }) {
  const queryClient = useQueryClient()
  const [banner, setBanner] = useState(null)

  const {
    control,
    handleSubmit,
    setError,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: { start_time: c.startLocal ?? '', end_time: c.endLocal ?? '' },
    mode: 'onTouched',
  })

  // Lock the page scroll and close on Escape while the dialog is up.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [onClose])

  const rescheduleMutation = useMutation({
    mutationFn: (payload) => api.liveClasses.update(c.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'live-classes'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'course-detail'] })
      onClose()
    },
    onError: (err) => {
      // Laravel 422s carry per-field messages under `details`; field names
      // match the payload keys 1:1, so they surface inline as-is.
      const details = err?.details
      if (details && typeof details === 'object') {
        for (const [key, msgs] of Object.entries(details)) {
          setError(key, { type: 'server', message: Array.isArray(msgs) ? msgs[0] : String(msgs) })
        }
      }
      setBanner(err?.message || 'Could not reschedule the class. Please try again.')
    },
  })

  const onSubmit = handleSubmit((v) => {
    setBanner(null)
    rescheduleMutation.mutate({
      start_time: toApiDateTime(v.start_time),
      end_time: toApiDateTime(v.end_time),
    })
  })

  const cls = (name) => `${inputCls} ${errors[name] ? 'border-red-300' : 'border-stone-200'}`
  const minStart = nowLocal()

  const modal = (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      {/* Click-away backdrop in the site's maroon, matching the hero glow. */}
      <div className="absolute inset-0 bg-[#3D0A0A]/60 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Reschedule Live Class"
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <span className="eyebrow text-amber-700">Vedic Academy</span>
            <h3 className="text-xl text-astroDark font-royal">Reschedule Class</h3>
            <p className="text-xs text-stone-500 mt-0.5">
              &ldquo;{c.title}&rdquo; — pick the new date and time. Enrolled students see the updated schedule.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 shrink-0 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50 flex items-center justify-center text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {banner && (
          <div className="mb-4 px-4 py-3 rounded-xl text-xs font-semibold bg-red-50 text-red-600 border border-red-100">
            {banner}
          </div>
        )}

        {(c.dateLabel && c.dateLabel !== '—') && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-stone-50 border border-stone-100 text-xs text-stone-500">
            Currently scheduled for <span className="font-semibold text-astroDark">{c.dateLabel}</span>
            {c.time ? <> at <span className="font-semibold text-astroDark">{c.time}{c.endTime ? ` – ${c.endTime}` : ''}</span></> : null}
          </div>
        )}

        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="New Start" error={errors.start_time?.message}>
            <Controller
              name="start_time"
              control={control}
              rules={{
                required: 'Start time is required',
                validate: (v) => v >= minStart || 'Start time must be now or later',
              }}
              render={({ field }) => (
                <DateTimeField
                  min={minStart}
                  className={cls('start_time')}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </Field>

          <Field label="New End" error={errors.end_time?.message}>
            <Controller
              name="end_time"
              control={control}
              rules={{
                required: 'End time is required',
                validate: (v) => {
                  const start = getValues('start_time')
                  return !start || v > start || 'End time must be after the start time'
                },
              }}
              render={({ field }) => (
                <DateTimeField
                  min={minStart}
                  className={cls('end_time')}
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </Field>

          <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-4 border-t border-stone-100">
            <button
              type="button"
              onClick={onClose}
              className="border border-stone-200 px-5 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={rescheduleMutation.isPending}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {rescheduleMutation.isPending ? 'Rescheduling…' : 'Reschedule Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  // Portal to <body> so the overlay escapes the dashboard's stacking context
  // and paints above the site's fixed header (z-50).
  return typeof document === 'undefined' ? null : createPortal(modal, document.body)
}
